import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MobileEmptyState from './MobileEmptyState';
import MobileLoadingSkeleton from './MobileLoadingSkeleton';
import StatusBadge from './StatusBadge';

const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(dateStr);
};

const formatRange = (start, end) => {
  const s = parseLocalDate(start);
  if (!s) return 'Date TBD';
  const e = end ? parseLocalDate(end) : null;
  const opts = { month: 'short', day: 'numeric' };
  if (!e || s.toDateString() === e.toDateString()) {
    return s.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  }
  if (s.getMonth() === e.getMonth()) {
    return `${s.toLocaleDateString('en-US', opts)} – ${e.getDate()}, ${e.getFullYear()}`;
  }
  return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
};

const orgBadge = (org) => {
  const o = (org || '').toUpperCase();
  const map = {
    UPCI: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    WPF: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    ALJC: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    AFF: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    AIM: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    PAW: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
  };
  return map[o] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
};

const MobileSchedule = ({ events = [] }) => {
  const [timeFilter, setTimeFilter] = useState('upcoming');
  const [orgFilter, setOrgFilter] = useState('All');

  const loading = events.length === 0;

  const filtered = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [...events]
      .filter((e) => {
        const d = parseLocalDate(e.end || e.start || e.date);
        if (!d) return false;
        if (timeFilter === 'upcoming' && d < today) return false;
        if (timeFilter === 'past' && d >= today) return false;
        if (orgFilter !== 'All' && (e.org || '').toUpperCase() !== orgFilter) return false;
        return true;
      })
      .sort((a, b) => {
        const da = parseLocalDate(a.start || a.date);
        const db = parseLocalDate(b.start || b.date);
        return timeFilter === 'past' ? db - da : da - db;
      });
  }, [events, timeFilter, orgFilter]);

  const orgs = useMemo(() => {
    const set = new Set(events.map((e) => (e.org || '').toUpperCase()).filter(Boolean));
    return ['All', ...Array.from(set).sort()];
  }, [events]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-4">
      {/* Header */}
      <header
        className="px-4 pb-4 bg-gradient-to-br from-primary-dark to-primary text-white rounded-b-3xl shadow-lg"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1.25rem)' }}
      >
        <h1 className="font-display text-2xl font-bold mb-1">Event Schedules</h1>
        <p className="text-white/70 text-sm">Browse all conference schedules</p>
      </header>

      {/* Time filter tabs */}
      <div className="px-4 mt-4">
        <div className="flex gap-2">
          <button
            onClick={() => setTimeFilter('upcoming')}
            className={`chip flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
              timeFilter === 'upcoming' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 shadow-sm'
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setTimeFilter('past')}
            className={`chip flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
              timeFilter === 'past' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 shadow-sm'
            }`}
          >
            Past
          </button>
        </div>
      </div>

      {/* Org filter chips */}
      {orgs.length > 2 && (
        <div className="px-4 mt-3">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
            {orgs.map((org) => (
              <button
                key={org}
                onClick={() => setOrgFilter(org)}
                className={`chip px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
                  orgFilter === org
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-sm'
                }`}
              >
                {org}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Schedule list */}
      <div className="mt-4 px-4 space-y-3">
        {loading ? (
          <MobileLoadingSkeleton variant="card" count={4} />
        ) : filtered.length === 0 ? (
          <MobileEmptyState
            icon={<svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            title="No events scheduled"
            message={`No ${timeFilter === 'past' ? 'past' : 'upcoming'} events${orgFilter !== 'All' ? ` for ${orgFilter}` : ''}.`}
          />
        ) : (
          filtered.map((e) => (
            <Link
              key={e.id}
              to={`/events/${e.id}`}
              className="block bg-white dark:bg-gray-800 rounded-2xl shadow-card active:scale-[0.98] transition-transform overflow-hidden"
            >
              <div className="flex gap-3 p-3">
                {/* Date block */}
                <div className="flex flex-col items-center justify-center w-14 shrink-0 bg-primary/5 dark:bg-primary/10 rounded-xl py-2">
                  <span className="text-[10px] font-semibold text-primary uppercase">
                    {parseLocalDate(e.start || e.date)?.toLocaleDateString('en-US', { month: 'short' })}
                  </span>
                  <span className="text-xl font-bold text-primary">
                    {parseLocalDate(e.start || e.date)?.getDate()}
                  </span>
                  <span className="text-[9px] text-gray-400">
                    {parseLocalDate(e.start || e.date)?.getFullYear()}
                  </span>
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {e.org && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${orgBadge(e.org)}`}>
                        {e.org}
                      </span>
                    )}
                    <StatusBadge status={e.status} size="sm" />
                  </div>
                  <h3 className="font-semibold text-sm text-gray-900 dark:text-white leading-snug line-clamp-2">
                    {e.event || e.title}
                  </h3>
                  <div className="mt-1.5 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      {formatRange(e.start || e.date, e.end)}
                    </span>
                    {e.city && (
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                        {e.city}
                      </span>
                    )}
                  </div>
                </div>

                {/* Chevron */}
                <svg className="w-4 h-4 text-gray-300 self-center shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
};

export default MobileSchedule;
